import React, { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import * as AiIcons from 'react-icons/ai';

import Context, { IContext } from '../../context/Context';
import Modal from '../Modal';
import FormularioRegistro from './FormularioRegistro';
import Paginacao from './Paginacao';

import api from '../../services/api';
import CustomAlert from '../CustomAlert';
import { ITipEsp } from '../../types/TipEsp';
import { tratarData } from '../../utils/tratarData';
import { IReserva } from '../../types/Reserva';
import { formatCurrency } from '../../utils/formatCurrency';
import { IConAdi } from '../../types/ConAdi';
import { IConvidado } from '../../types/Convidado';
import { ICheckout } from '../../types/Checkout';
import { IEspaco } from '../../types/Espaco';
import { ITipRes } from '../../types/TipRes';

export interface ICampos {
  campo: string;
  label: string;
  type?: 'text' | 'number' | 'date' | 'datetime-local' | 'email' | 'month' | 'range' | 'search' | 'tel' | 'time' | 'url' | 'week' | 'select' | 'checkbox' | 'radio' | 'file' | 'textarea';
  default?: any;
  options?: { value: string | number; label: string }[];
}

export interface IFormatoCampos {
  [key: string]: (value: any) => ReactNode;
}

interface ICadastroGenerico {
  entidade: string;
  endpoint: string;
  campos: ICampos[];
  formatoCampos?: IFormatoCampos;
  reserva?: IReserva;
  convidados?: IConvidado[]
}

interface IRegistro {
  [key: string]: any;
  editando?: boolean;
  cod?: number; // Adicionado para identificação de registros
}

function textoSemHtml(html: string) {
  return html.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
}

// Busca sem diferenciar maiúsculas/minúsculas e acentos
function normalizarBusca(texto: string) {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function ehUrlImagem(valor: any) {
  return !!valor && typeof valor === 'string' && valor.includes('https://');
}

function CadastroGenerico({ entidade, endpoint, campos, formatoCampos = {}, reserva, convidados }: ICadastroGenerico) {
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const { setIsLoadingOverlay, usuario }: IContext = useContext(Context);
  const [data, setData] = useState<IRegistro[]>([]);
  const [opcoesSelect, setOpcoesSelect] = useState<{ value: number; label: string }[]>([]);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [convidadosAdicionaisPagos, setConvidadosAdicionaisPagos] = useState<IConAdi[]>([]);
  const [quantidadeConvidadosAdicionaisPagos, setQuantidadeConvidadosAdicionaisPagos] = useState<number>(0);
  const [quantidadeConvidadosAdicionaisInput, setQuantidadeConvidadosAdicionaisInput] = useState<string>('0');
  const [busca, setBusca] = useState('');
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);


  async function getData() {
    try {
      if (endpoint === '/convidados' && !reserva?.cod) return;
      setIsLoadingOverlay(true);

      let params = {};

      if (endpoint === '/datasEspeciais') {
        params = { datini: tratarData(new Date().toISOString()) };
      }


      if (endpoint === '/convidados' && reserva?.cod) {
        params = { codres: reserva.cod };
      }

      const response = await api.get(endpoint, { params });

      if (response.status === 200) {
        setData(response.data);
      }
    } catch (error: any) {
      if (error.response?.data) return toast.error(error.response.data.erro);
      toast.error(`Erro ao buscar ${entidade}`);
    } finally {
      setTimeout(() => {
        setIsLoadingOverlay(false);
      }, 500);
    }
  }

  async function getDadosSelect(endpoint: string) {
    try {
      const response = await api.get(endpoint);

      if (response.status === 200) {
        let options = [];

        if (endpoint === '/tiposDeEspaco') {
          options = response.data.map((tipo: ITipEsp) => ({ value: tipo.cod, label: tipo.tipesp }));
        }

        if (endpoint === '/espacos') {
          options = response.data.map((espaco: IEspaco) => ({ value: espaco.cod, label: espaco.esp }));
        }

        if (endpoint === '/tiposDeReserva') {
          options = response.data.map((tipo: ITipRes) => ({ value: tipo.cod, label: tipo.des }));
        }

        setOpcoesSelect(options);
      }
    } catch (error: any) {
      toast.error(`Erro ao buscar ${endpoint}`);
    }
  }

  async function getConvidadosAdicionais() {
    try {
      if (endpoint === '/convidados' && !reserva?.cod) return;

      const response = await api.get('/convidadosAdicionais', {
        params: {
          codres: reserva!.cod
        }
      });

      if (response.status === 200) {
        const convidadosAdicionaisPagos = response.data.filter((convidadoAdicional: IConAdi) => convidadoAdicional.sta === 'PAGO');
        const totalQtdConvidadosAdicionaisPagos = convidadosAdicionaisPagos.reduce((total: number, convidadoAdicional: IConAdi) => total + convidadoAdicional.quacon, 0);

        setConvidadosAdicionaisPagos(convidadosAdicionaisPagos);
        setQuantidadeConvidadosAdicionaisPagos(totalQtdConvidadosAdicionaisPagos);
      }
    } catch (error: any) {
      toast.error('Erro ao buscar Convidados Adicionais');
    }
  }

  async function postData(novoRegistro: IRegistro) {
    try {
      setIsLoadingOverlay(true);

      if (imageInputRef.current?.files && imageInputRef.current?.files?.length > 0) {
        const formData = new FormData();

        formData.append('file', imageInputRef.current.files[0]);
        for (const key in novoRegistro) {
          formData.append(key, novoRegistro[key]);
        }

        const response = await api.post(`${endpoint}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            codusu: usuario?.cod
          },
        });

        if (response.status === 201) {
          toast.success(`${entidade} criado com sucesso`);
          getData();
          return;
        }

        return;
      }

      const response = await api.post(endpoint, novoRegistro, {
        headers: {
          codusu: usuario?.cod
        }
      });

      if (response.status === 201) {
        toast.success(`${entidade} criado com sucesso`);
        getData();
      }
    } catch (error: any) {
      if (error.response?.data) {
        if (error.response.data.erro.includes('Limite de convidados atingido.')) return setModalVisible(true);

        return toast.error(error.response.data.erro);
      }

      toast.error(`Erro ao criar ${entidade}`);
    } finally {
      setTimeout(() => {
        setIsLoadingOverlay(false);
      }, 500);
    }
  }

  async function postConAdi() {
    try {
      if (!quantidadeConvidadosAdicionaisInput || +quantidadeConvidadosAdicionaisInput === 0) return toast.warning('Informe a quantidade de convidados adicionais');

      if (!reserva) return toast.warning('Falha ao buscar reserva');;

      const quantidadeConvidadosAdicionais = +quantidadeConvidadosAdicionaisInput;

      setIsLoadingOverlay(true);

      const payload: IConAdi = {
        codres: reserva.cod,
        quacon: quantidadeConvidadosAdicionais
      }

      const response = await api.post('/convidadosAdicionais', payload);

      if (response.status === 201) {
        const payloadCheckout: ICheckout = {
          cod: response.data.cod,
          cliente: {
            raz: usuario?.usu || 'Associado',
            tipo: 'PF',
            cod: usuario?.cod.toString() || '',
            cgc: usuario?.cgc || '',
            tipocgc: 'CPF'
          },
          produtos: [
            {
              valor: reserva.espaco.tipesp.valconadi,
              produto: `Convidado adicional - ${reserva.espaco.tipesp.tipesp}`,
              cod: response.data.cod,
              qua: quantidadeConvidadosAdicionais
            }
          ],
          pagamentos: [
            {
              checkout: {
                expiracao: 15,
                editavel: true,
                metodospagamento: ['credit_card', 'debit_card', 'pix'],
                urlredirecionamento: window.location.origin + '/usuario/minhasReservas',
                pix: {
                  expiracao: 900
                }
              },
              metodopagamento: 'checkout'
            }
          ]
        }

        const responseCheckout = await api.post('/checkout', payloadCheckout);

        if (responseCheckout.status === 200) {
          window.location.href = responseCheckout.data.checkoutUrl;
        }

      }

    } catch (error: any) {
      if (error.response?.data) return toast.error(error.response.data.erro);
      toast.error(`Erro ao Concluir Compra de Convidado Adicional`);
    } finally {
      setTimeout(() => {
        setIsLoadingOverlay(false);
      }, 500);
    }
  }

  async function putData(registro: IRegistro) {
    try {
      setIsLoadingOverlay(true);

      if (imageInputRef.current?.files && imageInputRef.current?.files?.length > 0) {
        const formData = new FormData();

        formData.append('file', imageInputRef.current.files[0]);
        for (const key in registro) {
          formData.append(key, registro[key]);
        }

        const response = await api.put(`${endpoint}/${registro.cod}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (response.status === 200) {
          toast.success(`${entidade} atualizado com sucesso`);
          getData();
          return;
        }

        return;
      }

      const response = await api.put(`${endpoint}/${registro.cod}`, registro);

      if (response.status === 200) {
        toast.success(`${entidade} atualizado com sucesso`);
        getData();
      }

    } catch (error: any) {
      if (error.response?.data) return toast.error(error.response.data.erro);
      toast.error(`Erro ao atualizar ${entidade}`);
    } finally {
      setTimeout(() => {
        setIsLoadingOverlay(false);
      }, 500);
    }
  }

  async function deleteData(id: number) {
    try {
      setIsLoadingOverlay(true);
      const response = await api.delete(`${endpoint}/${id}`);
      if (response.status === 204) {
        toast.success(`${entidade} excluído com sucesso`);
        getData();
      }
    } catch (error: any) {
      if (error.response?.data) return toast.error(error.response.data.erro);
      toast.error(`Erro ao excluir ${entidade}`);
    } finally {
      setTimeout(() => {
        setIsLoadingOverlay(false);
      }, 500);
    }
  }

  function handleButtonClick() {
    if (entidade === 'Convidado' && (reserva?.usuario.cod === Number(usuario?.cod)) && data.filter(item => !item.editando).length >= (reserva!.tipres.limcon + quantidadeConvidadosAdicionaisPagos)) {
      if (reserva!.espaco.tipesp.limconadi === 0 || reserva?.tipres.limcon === 0) {
        return toast.warning('Não permitido');
      }

      return setModalVisible(true);
    }

    const novoRegistro = data.find(item => item.editando);

    if (!novoRegistro) {
      const novoItem = campos.reduce((acc, campo) => ({
        ...acc,
        [campo.campo]: campo.default || '',
        editando: true
      }), {});
      setData([...data, novoItem]);
      return;
    }

    if (reserva?.cod) {
      novoRegistro['codres'] = reserva.cod;

      if (novoRegistro?.con.split(' ').length < 2) return toast.warning('Obrigatório informar Nome Completo do Convidado');
      if (entidade === 'Convidado' && (reserva?.usuario.cod !== Number(usuario?.cod)) && !novoRegistro.obs) return toast.warning('Administradores devem informar observação do convidado');
    }

    if (endpoint === '/espacos' || endpoint === '/tiposDeReserva') {
      novoRegistro['codtipesp'] = novoRegistro?.tipesp?.cod;
    }

    if (endpoint === '/datasEspeciais') {
      novoRegistro['codtipres'] = novoRegistro?.tipres?.cod;
    }

    delete novoRegistro['dathorins'];

    if (novoRegistro.cod) {
      putData(novoRegistro); // Atualiza o registro se um código existir
    } else {
      postData(novoRegistro); // Cria um novo registro se não houver código
    }
  }

  function editarRegistro(index: number) {
    setData(data.map((registro, i) => i === index ?
      { ...registro, editando: true } :
      { ...registro, editando: false }));
  }

  function cancelarEdicao() {
    const index = data.findIndex(item => item.editando);
    if (index < 0) return;

    if (data[index].cod) {
      setData(data.map((registro, i) => i === index ? { ...registro, editando: false } : registro));
      return;
    }

    setData(data.filter((_, i) => i !== index));
  }

  function excluirRegistro(item: IRegistro) {
    CustomAlert({
      title: `Exclusão de ${entidade}`,
      message: `Deseja prosseguir com a exclusão?`,
      onConfirm: async () => {
        await deleteData(item.cod!);
      },
      onCancel: () => { }
    });
  }

  function alterarCampoEmEdicao(campo: string, valor: any) {
    setData(data.map(registro => registro.editando ? { ...registro, [campo]: valor } : registro));
  }

  // Cada entidade grava o select numa chave diferente (regra mantida do cadastro inline)
  function alterarSelectEmEdicao(valor: number) {
    if (entidade === 'Foto do Espaço') {
      setData(data.map(registro => registro.editando ? { ...registro, codesp: valor } : registro));
      return;
    }

    if (entidade === 'Datas Especiais') {
      setData(data.map(registro => registro.editando ? { ...registro, tipres: { cod: valor } } : registro));
      return;
    }

    setData(data.map(registro => registro.editando ? { ...registro, tipesp: { cod: valor } } : registro));
  }

  function valorSelectDoRegistro(item?: IRegistro) {
    if (!item) return '';

    const valor = entidade === 'Foto do Espaço' ? item.codesp :
      entidade === 'Datas Especiais' ? (item.tipres?.cod ?? item.codtipres) :
        item.tipesp?.cod;

    return valor ? valor.toString() : '';
  }

  function rotuloSelect(item: IRegistro) {
    return opcoesSelect.find(
      option => option.value === item?.tipesp?.cod || option.value === item?.codesp || option.value === item?.codtipres
    )?.label;
  }

  // Texto do campo como exibido na lista, usado na busca
  function textoCampo(campo: ICampos, item: IRegistro): string {
    const valor = item[campo.campo];

    if (campo.type === 'select') return rotuloSelect(item) || '';
    if (campo.type === 'checkbox') return valor ? 'Sim' : 'Não';
    if (ehUrlImagem(valor)) return '';

    if (formatoCampos[campo.campo]) {
      const formatado = formatoCampos[campo.campo](valor);
      if (typeof formatado === 'string' || typeof formatado === 'number') return String(formatado);
    }

    return valor === null || valor === undefined ? '' : textoSemHtml(String(valor));
  }

  function renderCampo(campo: ICampos, item: IRegistro) {
    const valor = item[campo.campo];

    if (ehUrlImagem(valor)) {
      return (
        <img src={valor} alt={campo.label} className='h-12 w-16 rounded-lg object-cover' />
      );
    }

    if (campo.type === 'select') return rotuloSelect(item) || <span className='text-zinc-300'>—</span>;

    if (campo.type === 'checkbox') {
      return (
        <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${valor ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}>
          {valor ? 'Sim' : 'Não'}
        </span>
      );
    }

    if (formatoCampos[campo.campo]) return formatoCampos[campo.campo](valor);

    if (valor === null || valor === undefined || valor === '') return <span className='text-zinc-300'>—</span>;

    if (typeof valor === 'string' && /<[^>]+>/.test(valor)) {
      return <span className='line-clamp-2'>{textoSemHtml(valor)}</span>;
    }

    return valor;
  };

  useEffect(() => {
    if (entidade === 'Convidado' && convidados) {
      setData(convidados)
      getConvidadosAdicionais();
      return;
    }

    if (entidade === 'Espaço' || entidade === 'Tipo de Reserva') {
      getDadosSelect('/tiposDeEspaco');
    }

    if (entidade === 'Datas Especiais') {
      getDadosSelect('/tiposDeReserva');
    }

    if (entidade === 'Foto do Espaço') {
      getDadosSelect('/espacos');
    }

    getData();
  }, []);

  const ehConvidado = entidade === 'Convidado';
  const limiteConvidados = reserva ? reserva.tipres.limcon + quantidadeConvidadosAdicionaisPagos : 0;
  const exibirAumentarLimite = ehConvidado && !!reserva &&
    !(reserva.espaco.tipesp.limconadi === 0 && (reserva.usuario.cod === Number(usuario?.cod))) &&
    reserva.tipres.limcon !== 0;
  const exibirAssociado = usuario?.tip === 'ADMINISTRADOR' || usuario?.tip === 'ASSISTENTE';

  const indiceEmEdicao = data.findIndex(item => item.editando);
  const registroEmEdicao = indiceEmEdicao >= 0 ? data[indiceEmEdicao] : undefined;

  // Mantém o índice original de cada item para que editar/cancelar continuem apontando para o registro certo em `data`
  const termoBusca = normalizarBusca(busca.trim());
  const registrosFiltrados = data
    .map((item, indiceOriginal) => ({ item, indiceOriginal }))
    .filter(({ item }) => !item.editando)
    .filter(({ item }) => !termoBusca || campos.some(campo => normalizarBusca(textoCampo(campo, item)).includes(termoBusca)));

  const totalPaginas = Math.max(1, Math.ceil(registrosFiltrados.length / itensPorPagina));
  const paginaExibida = Math.min(paginaAtual, totalPaginas);
  const registrosDaPagina = registrosFiltrados.slice((paginaExibida - 1) * itensPorPagina, paginaExibida * itensPorPagina);

  useEffect(() => {
    // Ao excluir o último item da última página, volta para a página anterior
    if (paginaAtual > totalPaginas) setPaginaAtual(totalPaginas);
  }, [totalPaginas]);

  const botaoNovo = (
    <button
      type='button'
      onClick={handleButtonClick}
      className='flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-zinc-900 px-5 font-semibold text-white transition-colors hover:bg-zinc-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2 md:h-10 md:w-auto md:px-4 md:text-sm'
    >
      <AiIcons.AiOutlinePlus aria-hidden='true' />
      {ehConvidado ? 'Adicionar convidado' : 'Novo'}
    </button>
  );

  const botaoAumentarLimite = exibirAumentarLimite && (
    <button
      type='button'
      onClick={() => setModalVisible(true)}
      className='flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-zinc-200 bg-white px-5 font-semibold text-zinc-900 transition-colors hover:bg-zinc-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 md:h-10 md:w-auto md:px-4 md:text-sm'
    >
      <AiIcons.AiOutlineUsergroupAdd aria-hidden='true' />
      Aumentar limite
    </button>
  );

  function botoesAcao(item: IRegistro, indiceOriginal: number) {
    return (
      <div className='flex items-center justify-end gap-1'>
        <button
          type='button'
          onClick={() => editarRegistro(indiceOriginal)}
          aria-label={`Editar ${entidade}`}
          className='flex h-10 w-10 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 md:h-9 md:w-9'
        >
          <AiIcons.AiOutlineEdit className='h-5 w-5' aria-hidden='true' />
        </button>
        <button
          type='button'
          onClick={() => excluirRegistro(item)}
          aria-label={`Excluir ${entidade}`}
          className='flex h-10 w-10 items-center justify-center rounded-full text-zinc-500 hover:bg-rose-50 hover:text-rose-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-600 md:h-9 md:w-9'
        >
          <AiIcons.AiOutlineDelete className='h-5 w-5' aria-hidden='true' />
        </button>
      </div>
    );
  }

  return (
    <div className='w-full max-w-5xl mx-auto px-4 pb-32 md:pb-10'>
      {(ehConvidado && reserva) &&
        <Modal modalVisible={modalVisible}
          setModalVisible={setModalVisible}
          onConfirm={postConAdi}
          setQuantidadeConvidadosAdicionaisInput={setQuantidadeConvidadosAdicionaisInput}
          titulo='Aumentar limite de convidados'
          descricao={`${formatCurrency(reserva.espaco.tipesp.valconadi)} por convidado · até ${reserva.espaco.tipesp.limconadi - quantidadeConvidadosAdicionaisPagos} adicionais`}
          valorUnitario={reserva.espaco.tipesp.valconadi}
        />}

      <FormularioRegistro
        aberto={!!registroEmEdicao}
        titulo={registroEmEdicao?.cod ? `Editar ${entidade}` : `Adicionar ${entidade}`}
        campos={campos}
        registro={registroEmEdicao}
        valorSelect={valorSelectDoRegistro(registroEmEdicao)}
        opcoesSelect={opcoesSelect}
        imageInputRef={imageInputRef}
        onChange={alterarCampoEmEdicao}
        onSelect={alterarSelectEmEdicao}
        onSalvar={handleButtonClick}
        onCancelar={cancelarEdicao}
      />

      <header className='mb-6 mt-4 flex flex-wrap items-end justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-semibold text-zinc-900 md:text-3xl'>{ehConvidado ? 'Lista de Convidados' : `Cadastro de ${entidade}`}</h1>
          <p className='mt-1 text-zinc-500' aria-live='polite'>
            {ehConvidado && reserva ?
              `${data.length} de ${limiteConvidados} ${limiteConvidados === 1 ? 'convidado' : 'convidados'}` :
              `${data.length} ${data.length === 1 ? 'registro' : 'registros'}`}
          </p>
        </div>
        <div className='hidden items-center gap-2 md:flex'>
          {botaoAumentarLimite}
          {botaoNovo}
        </div>
      </header>

      {(ehConvidado && reserva) && (
        <section className='mb-6 rounded-2xl border border-zinc-200 bg-white p-5 shadow-card'>
          <dl className='grid grid-cols-2 gap-4 text-sm md:grid-cols-4'>
            <div>
              <dt className='text-zinc-500'>Data</dt>
              <dd className='mt-0.5 font-semibold text-zinc-900'>{reserva.datres}</dd>
            </div>
            <div>
              <dt className='text-zinc-500'>Espaço</dt>
              <dd className='mt-0.5 font-semibold text-zinc-900'>{reserva.espaco.esp}</dd>
            </div>
            {reserva.tipres?.des && (
              <div>
                <dt className='text-zinc-500'>Tipo de reserva</dt>
                <dd className='mt-0.5 font-semibold text-zinc-900'>{reserva.tipres.des}</dd>
              </div>
            )}
            {exibirAssociado && (
              <div>
                <dt className='text-zinc-500'>Associado</dt>
                <dd className='mt-0.5 font-semibold text-zinc-900'>{reserva.usuario?.usu}</dd>
              </div>
            )}
          </dl>
          {limiteConvidados > 0 && (
            <div className='mt-4 border-t border-zinc-100 pt-4'>
              <div className='mb-1.5 flex justify-between text-xs text-zinc-500'>
                <span>Convidados</span>
                <span>{data.length}/{limiteConvidados}</span>
              </div>
              <div className='h-2 overflow-hidden rounded-full bg-zinc-100'>
                <div
                  className={`h-full rounded-full transition-all ${data.length >= limiteConvidados ? 'bg-amber-500' : 'bg-zinc-900'}`}
                  style={{ width: `${Math.min(100, (data.length / limiteConvidados) * 100)}%` }}
                />
              </div>
            </div>
          )}
        </section>
      )}

      <div className='relative mb-4'>
        <AiIcons.AiOutlineSearch className='pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-zinc-400' aria-hidden='true' />
        <input
          type='search'
          value={busca}
          onChange={(e) => {
            setBusca(e.target.value);
            setPaginaAtual(1);
          }}
          placeholder='Buscar…'
          aria-label={`Buscar ${ehConvidado ? 'convidados' : entidade}`}
          className='h-12 w-full rounded-xl border border-zinc-300 bg-white pl-11 pr-11 text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900/10 [&::-webkit-search-cancel-button]:hidden'
        />
        {busca && (
          <button
            type='button'
            onClick={() => {
              setBusca('');
              setPaginaAtual(1);
            }}
            aria-label='Limpar busca'
            className='absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'
          >
            <AiIcons.AiOutlineClose className='h-4 w-4' aria-hidden='true' />
          </button>
        )}
      </div>

      <section className='overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-card'>
        {registrosFiltrados.length === 0 ? (
          <div className='flex flex-col items-center gap-2 px-6 py-12 text-center text-sm text-zinc-400'>
            <AiIcons.AiOutlineInbox className='h-8 w-8' aria-hidden='true' />
            {termoBusca ? (
              <>
                Nenhum resultado para “{busca.trim()}”.
                <button
                  type='button'
                  onClick={() => setBusca('')}
                  className='font-semibold text-zinc-900 underline underline-offset-4 hover:text-zinc-600'
                >
                  Limpar busca
                </button>
              </>
            ) : (
              ehConvidado ? 'Nenhum convidado cadastrado.' : 'Nenhum registro cadastrado.'
            )}
          </div>
        ) : (
          <>
            <div className='hidden overflow-x-auto md:block'>
              <table className='w-full text-left text-sm'>
                <thead className='bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500'>
                  <tr>
                    {campos.map(campo => (
                      <th key={campo.campo} scope='col' className='whitespace-nowrap px-4 py-3 font-medium'>{campo.label}</th>
                    ))}
                    {/* Coluna de ações fixa à direita para não precisar rolar até o fim em tabelas com muitas colunas */}
                    <th scope='col' className='sticky right-0 z-10 bg-zinc-50 px-4 py-3 text-right font-medium shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]'><span className='sr-only'>Ações</span></th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-zinc-100'>
                  {registrosDaPagina.map(({ item, indiceOriginal }) => (
                    <tr key={item.cod ?? `novo-${indiceOriginal}`} className='group text-zinc-700 hover:bg-zinc-50'>
                      {campos.map((campo, i) => (
                        <td key={campo.campo} className={`max-w-xs px-4 py-3 align-middle ${i === 0 ? 'font-medium text-zinc-900' : ''}`}>
                          {renderCampo(campo, item)}
                        </td>
                      ))}
                      <td className='sticky right-0 z-10 whitespace-nowrap bg-white px-4 py-2 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)] group-hover:bg-zinc-50'>
                        {botoesAcao(item, indiceOriginal)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className='divide-y divide-zinc-100 md:hidden'>
              {registrosDaPagina.map(({ item, indiceOriginal }) => {
                const [primeiroCampo, ...demaisCampos] = campos;

                return (
                  <li key={item.cod ?? `novo-${indiceOriginal}`} className='flex items-start gap-2 px-4 py-4'>
                    <div className='min-w-0 flex-1'>
                      <div className='font-semibold text-zinc-900 break-words'>{renderCampo(primeiroCampo, item)}</div>
                      {demaisCampos.length > 0 && (
                        <dl className='mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm'>
                          {demaisCampos.map(campo => (
                            <React.Fragment key={campo.campo}>
                              <dt className='text-zinc-500'>{campo.label}</dt>
                              <dd className='min-w-0 break-words text-zinc-900'>{renderCampo(campo, item)}</dd>
                            </React.Fragment>
                          ))}
                        </dl>
                      )}
                    </div>
                    {botoesAcao(item, indiceOriginal)}
                  </li>
                );
              })}
            </ul>

            {registrosFiltrados.length > 10 && (
              <Paginacao
                paginaAtual={paginaExibida}
                totalPaginas={totalPaginas}
                totalItens={registrosFiltrados.length}
                itensPorPagina={itensPorPagina}
                onMudarPagina={setPaginaAtual}
                onMudarItensPorPagina={(quantidade) => {
                  setItensPorPagina(quantidade);
                  setPaginaAtual(1);
                }}
              />
            )}
          </>
        )}
      </section>

      <div className='fixed inset-x-0 bottom-0 z-40 flex gap-2 border-t border-zinc-200 bg-white/95 px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur md:hidden'>
        {botaoAumentarLimite && <div className='flex-1'>{botaoAumentarLimite}</div>}
        <div className='flex-1'>{botaoNovo}</div>
      </div>
    </div>
  );
}

export default CadastroGenerico;
